import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  textFieldStyles,
  fonts,
  selectStyles,
  mainButtonStyles,
  headingFonts,
} from "../../../../Styles/Styles";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Container,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ReactQuill from "react-quill"; // Import React Quill
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import ImageUpload from "./ImageUpload";
import AdStatusModal from "../PostAd/AdStatusModal";

export default function EditAd() {
  const { uniqueId } = useParams();
  const [adDetails, setAdDetails] = useState({
    title: "",
    description: "",
    price: "",
    images: [],
    category: "",
    model: "",
    year: "",
    engineSize: "",
    location: "",
    receiveOffers: false, // Initial value for receive offers
    minOfferAmount: "", // Initial value for minimum offer amount
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [imageOrder, setImageOrder] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]); // State for locations
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
    adID: null,
  });

  useEffect(() => {
    const fetchAdDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/${uniqueId}`
        );
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();

        setAdDetails(data);

        // Convert image URLs into objects for consistency with new uploads
        const sortedImages = data.images.map((url, index) => ({
          id: url.thumbnail, // Use URL as ID for existing images
          // id: `${index}-${url.original}`,
          name: `Image ${index + 1}`,
          isNew: false,
        }));

        setImages(sortedImages);
        setImageOrder(data.images); // Store initial order
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/categories`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const categoriesData = await response.json();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const fetchLocations = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/locations`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch locations");
        }
        const locationsData = await response.json();
        setLocations(locationsData);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchAdDetails();
    fetchCategories();
    fetchLocations();
  }, [uniqueId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    const model = adDetails.category === "Vehicles" ? adDetails.model : "";
    const year = adDetails.category === "Vehicles" ? adDetails.year : "";
    const make = adDetails.category === "Vehicles" ? adDetails.make : "";
    const engineSize =
      adDetails.category === "Vehicles" ? adDetails.engineSize : "";
    const bodyStyle =
      adDetails.category === "Vehicles" ? adDetails.bodyStyle : "";
    const trimLevel =
      adDetails.category === "Vehicles" ? adDetails.trimLevel : "";
    const transmission =
      adDetails.category === "Vehicles" ? adDetails.transmission : "";
    const drivetrain =
      adDetails.category === "Vehicles" ? adDetails.drivetrain : "";
    const fuelType =
      adDetails.category === "Vehicles" ? adDetails.fuelType : "";
    const mileage = adDetails.category === "Vehicles" ? adDetails.mileage : "";
    const mileageUnit =
      adDetails.category === "Vehicles" ? adDetails.mileageUnit : "";
    const condition =
      adDetails.category === "Vehicles" ? adDetails.condition : "";
    const interiorFeatures =
      adDetails.category === "Vehicles" ? adDetails.interiorFeatures : "";

    const propertyType =
      adDetails.category === "Real Estate" ? adDetails.propertyType : "";
    const listingType =
      adDetails.category === "Real Estate" ? adDetails.listingType : "";
    const availableFrom =
      adDetails.category === "Real Estate" ? adDetails.availableFrom : "";
    const bedrooms =
      adDetails.category === "Real Estate" ? adDetails.bedrooms : "";
    const bathrooms =
      adDetails.category === "Real Estate" ? adDetails.bathrooms : "";
    const squareFootage =
      adDetails.category === "Real Estate" ? adDetails.squareFootage : "";
    const lotSize =
      adDetails.category === "Real Estate" ? adDetails.lotSize : "";
    const yearBuilt =
      adDetails.category === "Real Estate" ? adDetails.yearBuilt : "";
    const furnishingStatus =
      adDetails.category === "Real Estate" ? adDetails.furnishingStatus : "";
    const parking =
      adDetails.category === "Real Estate" ? adDetails.parking : "";
    const floorLevel =
      adDetails.category === "Real Estate" ? adDetails.floorLevel : "";
    const totalFloors =
      adDetails.category === "Real Estate" ? adDetails.totalFloors : "";

    formData.append("title", adDetails.title);
    formData.append("description", adDetails.description);
    formData.append("price", adDetails.price);
    formData.append("category", adDetails.category);
    formData.append("location", adDetails.location);
    formData.append("receiveOffers", adDetails.receiveOffers); // Add receiveOffers to form data
    formData.append("minOfferAmount", adDetails.minOfferAmount); // Add minOfferAmount to form data
    // Vehicle category Fields
    formData.append("model", model);
    formData.append("year", year);
    formData.append("make", make);
    formData.append("engineSize", engineSize);
    formData.append("bodyStyle", bodyStyle);
    formData.append("trimLevel", trimLevel);
    formData.append("transmission", transmission);
    formData.append("drivetrain", drivetrain);
    formData.append("fuelType", fuelType);
    formData.append("mileage", mileage);
    formData.append("mileageUnit", mileageUnit);
    formData.append("condition", condition);
    formData.append("interiorFeatures", interiorFeatures);
    // Real Estate category Fields
    formData.append("propertyType", propertyType);
    formData.append("listingType", listingType);
    formData.append("availableFrom", availableFrom);
    formData.append("bedrooms", bedrooms);
    formData.append("bathrooms", bathrooms);
    formData.append("squareFootage", squareFootage);
    formData.append("lotSize", lotSize);
    formData.append("yearBuilt", yearBuilt);
    formData.append("furnishingStatus", furnishingStatus);
    formData.append("parking", parking);
    formData.append("floorLevel", floorLevel);
    formData.append("totalFloors", totalFloors);

    // Append removed images
    removedImages.forEach((image) => {
      formData.append("removedImages", image);
    });

    // Append imageOrder to formData
    imageOrder.forEach((image) => {
      formData.append("imageOrder", image.file);
      console.log("Image Order:", image.file);
    });

    images.forEach((image) => {
      if (image.isNew) {
        console.log("Appending new image:", image.file);
        formData.append("images", image.file); // ✅ Ensure new files are appended
      } else {
        console.log("Appending existing image:", image.id);
        formData.append("existingImages", image.id); // ✅ Ensure existing images are retained
      }
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/editad/${uniqueId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setStatusModal({
          open: true,
          type: "error",
          message: result.error || "You don't have permission to edit this Ad",
          adID: uniqueId,
        });
        return;
      }

      setStatusModal({
        open: true,
        type: "success",
        message: result.message || "Ad updated successfully!",
        adID: uniqueId,
      });
    } catch (error) {
      setStatusModal({
        open: true,
        type: "error",
        message: error.message || "An unexpected error occurred",
        adID: uniqueId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (error) return <div>{error}</div>;
  if (loading) return <LoadingAnimation />;

  return (
    <Container component="main" maxWidth="md">
      <Helmet>
        <title>Edit Ad - Listed.ky</title>
        <meta name="description" content="Edit Ad - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          width: "100%",
          minWidth: { sm: "100%", md: "100%" },
          padding: "24px",
          borderRadius: "12px",
          border: "1px solid #ddd",
          backgroundColor: "white",
          textAlign: "start",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            border: "1px solid var(--primary-color)",
          },
        }}
      >
        <Link
          to={"/my-ads"}
          style={{ textDecoration: "none", marginBottom: 15 }}
        >
          <Button variant="outlined" size="small">
            {"<"} Back to My Ads
          </Button>
        </Link>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            ...headingFonts,
            color: "var(--secondary-color)",
          }}
        >
          Edit Ad
        </Typography>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            value={adDetails.title}
            onChange={(e) =>
              setAdDetails({ ...adDetails, title: e.target.value })
            }
            required
            margin="normal"
            sx={{ ...textFieldStyles }}
          />
          <FormControl fullWidth required margin="normal">
            <InputLabel sx={{ ...fonts }}>Category</InputLabel>
            <Select
              label="Category"
              value={adDetails.category}
              onChange={(e) =>
                setAdDetails({ ...adDetails, category: e.target.value })
              }
              sx={{ ...selectStyles }}
            >
              <MenuItem value="" sx={{ ...fonts }}>
                <em>Select a category</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem
                  key={category._id}
                  value={category.label}
                  sx={{ ...fonts }}
                >
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {adDetails.category === "Real Estate" && (
            <>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Property Type */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Property Type</InputLabel>
                  <Select
                    label="Property Type"
                    value={adDetails.propertyType}
                    onChange={(e) =>
                      setAdDetails({
                        ...adDetails,
                        propertyType: e.target.value,
                      })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="House" sx={{ ...fonts }}>
                      House
                    </MenuItem>
                    <MenuItem value="Apartment" sx={{ ...fonts }}>
                      Apartment
                    </MenuItem>
                    <MenuItem value="Condo" sx={{ ...fonts }}>
                      Condo
                    </MenuItem>
                    <MenuItem value="Commercial" sx={{ ...fonts }}>
                      Commercial
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Listing Type */}
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Listing Type</InputLabel>
                  <Select
                    label="Listing Type"
                    value={adDetails.listingType}
                    onChange={(e) =>
                      setAdDetails({
                        ...adDetails,
                        listingType: e.target.value,
                      })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="For Sale" sx={{ ...fonts }}>
                      For Sale
                    </MenuItem>
                    <MenuItem value="For Rent" sx={{ ...fonts }}>
                      For Rent
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* Available From */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Available From"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={
                    adDetails.availableFrom
                      ? new Date(adDetails.availableFrom)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    setAdDetails({
                      ...adDetails,
                      availableFrom: e.target.value,
                    })
                  }
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Furnishing Status */}
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Furnishing Status</InputLabel>
                  <Select
                    label="Furnishing Status"
                    value={adDetails.furnishingStatus}
                    onChange={(e) =>
                      setAdDetails({
                        ...adDetails,
                        furnishingStatus: e.target.value,
                      })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Furnished" sx={{ ...fonts }}>
                      Furnished
                    </MenuItem>
                    <MenuItem value="Semi-Furnished" sx={{ ...fonts }}>
                      Semi-Furnished
                    </MenuItem>
                    <MenuItem value="Unfurnished" sx={{ ...fonts }}>
                      Unfurnished
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* Number of Bedrooms */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Number of Bedrooms"
                  type="number"
                  value={adDetails.bedrooms}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, bedrooms: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />

                {/* Number of Bathrooms */}
                <TextField
                  fullWidth
                  margin="normal"
                  required
                  label="Number of Bathrooms"
                  type="number"
                  value={adDetails.bathrooms}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, bathrooms: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Year Built */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Year Built"
                  type="number"
                  value={adDetails.yearBuilt}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, yearBuilt: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />
                {/* Square Footage */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Square Footage (sq. ft.)"
                  type="number"
                  value={adDetails.squareFootage}
                  onChange={(e) =>
                    setAdDetails({
                      ...adDetails,
                      squareFootage: e.target.value,
                    })
                  }
                  sx={{ ...textFieldStyles }}
                />
                {/* Lot Size */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Lot Size (if applicable)"
                  type="number"
                  value={adDetails.lotSize}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, lotSize: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />
              </Box>

              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                {/* Floor Level */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Floor Level (if applicable)"
                  type="number"
                  value={adDetails.floorLevel}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, floorLevel: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />

                {/* Total Floors in Building */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Total Floors in Building"
                  type="number"
                  value={adDetails.totalFloors}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, totalFloors: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />
                {/* Parking */}
                <TextField
                  fullWidth
                  margin="normal"
                  label="Parking (e.g., Garage, Street Parking, Number of Spaces)"
                  value={adDetails.parking}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, parking: e.target.value })
                  }
                  sx={{ ...textFieldStyles }}
                />
              </Box>
            </>
          )}
          {adDetails.category === "Vehicles" && (
            <>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <TextField
                  fullWidth
                  label="Make"
                  variant="outlined"
                  value={adDetails.make}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, make: e.target.value })
                  }
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  fullWidth
                  label="Model"
                  variant="outlined"
                  value={adDetails.model}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, model: e.target.value })
                  }
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  variant="outlined"
                  value={adDetails.year}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, year: e.target.value })
                  }
                  required
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  fullWidth
                  label="Trim Level"
                  type="text"
                  variant="outlined"
                  value={adDetails.trimLevel}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, trimLevel: e.target.value })
                  }
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Fuel Type</InputLabel>
                  <Select
                    label="Fuel Type"
                    value={adDetails.fuelType}
                    onChange={(e) =>
                      setAdDetails({ ...adDetails, fuelType: e.target.value })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Gasoline" sx={{ ...fonts }}>
                      Gasoline
                    </MenuItem>
                    <MenuItem value="Diesel" sx={{ ...fonts }}>
                      Diesel
                    </MenuItem>
                    <MenuItem value="Electric" sx={{ ...fonts }}>
                      Electric
                    </MenuItem>
                    <MenuItem value="Hybrid" sx={{ ...fonts }}>
                      Hybrid
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Body Style</InputLabel>
                  <Select
                    label="Body Style"
                    value={adDetails.bodyStyle}
                    onChange={(e) =>
                      setAdDetails({ ...adDetails, bodyStyle: e.target.value })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Sedan" sx={{ ...fonts }}>
                      Sedan
                    </MenuItem>
                    <MenuItem value="SUV" sx={{ ...fonts }}>
                      SUV
                    </MenuItem>
                    <MenuItem value="Truck" sx={{ ...fonts }}>
                      Truck
                    </MenuItem>
                    {/* Add more body styles as needed */}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Transmission</InputLabel>
                  <Select
                    label="Transmission"
                    value={adDetails.transmission}
                    onChange={(e) =>
                      setAdDetails({
                        ...adDetails,
                        transmission: e.target.value,
                      })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Automatic" sx={{ ...fonts }}>
                      Automatic
                    </MenuItem>
                    <MenuItem value="Manual" sx={{ ...fonts }}>
                      Manual
                    </MenuItem>
                    <MenuItem value="CVT" sx={{ ...fonts }}>
                      CVT
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel sx={{ ...fonts }}>Drivetrain</InputLabel>
                  <Select
                    label="Drivetrain"
                    value={adDetails.drivetrain}
                    onChange={(e) =>
                      setAdDetails({ ...adDetails, drivetrain: e.target.value })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="FWD" sx={{ ...fonts }}>
                      FWD
                    </MenuItem>
                    <MenuItem value="AWD" sx={{ ...fonts }}>
                      AWD
                    </MenuItem>
                    <MenuItem value="RWD" sx={{ ...fonts }}>
                      RWD
                    </MenuItem>
                    <MenuItem value="4WD" sx={{ ...fonts }}>
                      4WD
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{
                  display: { xs: "block", md: "flex" }, // Stack on smaller screens, flex on desktop
                  gap: 2, // Add spacing between items
                }}
              >
                <TextField
                  label="Engine Size"
                  variant="outlined"
                  fullWidth
                  required
                  value={adDetails.engineSize}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, engineSize: e.target.value })
                  }
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <TextField
                  label="Mileage"
                  type="number"
                  variant="outlined"
                  value={adDetails.mileage}
                  onChange={(e) =>
                    setAdDetails({ ...adDetails, mileage: e.target.value })
                  }
                  required
                  fullWidth
                  margin="normal"
                  sx={{ ...textFieldStyles }}
                />
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Mileage in</InputLabel>
                  <Select
                    label="Mileage Unit"
                    value={adDetails.mileageUnit}
                    onChange={(e) =>
                      setAdDetails({
                        ...adDetails,
                        mileageUnit: e.target.value,
                      })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Miles" sx={{ ...fonts }}>
                      Miles
                    </MenuItem>
                    <MenuItem value="Kilometers" sx={{ ...fonts }}>
                      Kilometers
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel sx={{ ...fonts }}>Condition</InputLabel>
                  <Select
                    label="Condition"
                    value={adDetails.condition}
                    onChange={(e) =>
                      setAdDetails({ ...adDetails, condition: e.target.value })
                    }
                    sx={{ ...selectStyles }}
                  >
                    <MenuItem value="Excellent" sx={{ ...fonts }}>
                      Excellent
                    </MenuItem>
                    <MenuItem value="Good" sx={{ ...fonts }}>
                      Good
                    </MenuItem>
                    <MenuItem value="Fair" sx={{ ...fonts }}>
                      Fair
                    </MenuItem>
                    <MenuItem value="Needs Work" sx={{ ...fonts }}>
                      Needs Work
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <ReactQuill
                className="quill-editor"
                value={adDetails.interiorFeatures}
                onChange={(value) =>
                  setAdDetails({ ...adDetails, interiorFeatures: value })
                }
                theme="snow"
                placeholder="Enter interior features (e.g., Leather Seats, Heated Seats)"
                required
              />
            </>
          )}
          {adDetails.category !== "Vehicles" && (
            <ReactQuill
              className="quill-editor"
              value={adDetails.description}
              onChange={(value) =>
                setAdDetails({ ...adDetails, description: value })
              }
              theme="snow"
              placeholder="Enter a detailed description of your ad"
              required
            />
          )}
          <FormControl fullWidth required margin="normal">
            <InputLabel sx={{ ...fonts }}>Location</InputLabel>
            <Select
              label="Location"
              value={adDetails.location}
              onChange={(e) =>
                setAdDetails({ ...adDetails, location: e.target.value })
              }
              sx={{ ...selectStyles }}
            >
              <MenuItem value="" sx={{ ...fonts }}>
                <em>Select a location</em>
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc.name} sx={{ ...fonts }}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Price"
            type="number"
            variant="outlined"
            fullWidth
            value={adDetails.price}
            onChange={(e) =>
              setAdDetails({ ...adDetails, price: e.target.value })
            }
            required
            margin="normal"
            sx={{ ...textFieldStyles }}
          />
          <ImageUpload
            images={images}
            setImages={setImages}
            removedImages={removedImages}
            setRemovedImages={setRemovedImages}
            setImageOrder={setImageOrder}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={adDetails.receiveOffers}
                onChange={(e) =>
                  setAdDetails({
                    ...adDetails,
                    receiveOffers: e.target.checked,
                  })
                }
                sx={{
                  "&.Mui-checked": {
                    color: "var(--primary-color)", // Filled color when checked
                  },
                }}
              />
            }
            label=<Typography sx={{ ...fonts }}>Receive Offers</Typography>
          />
          {/* {adDetails.receiveOffers && (
            <TextField
              label="Minimum Offer Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={adDetails.minOfferAmount}
              onChange={(e) =>
                setAdDetails({ ...adDetails, minOfferAmount: e.target.value })
              }
              margin="normal"
              required
              sx={{ ...textFieldStyles }}
            />
          )} */}
          {adDetails.receiveOffers && (
            <TextField
              label="Minimum Offer Amount"
              type="number"
              variant="outlined"
              fullWidth
              value={adDetails.minOfferAmount}
              onChange={(e) => {
                const value = e.target.value;
                // ✅ Only allow value <= price
                if (
                  parseFloat(value) <= parseFloat(adDetails.price) ||
                  value === ""
                ) {
                  setAdDetails({ ...adDetails, minOfferAmount: value });
                }
              }}
              margin="normal"
              required
              sx={{ ...textFieldStyles }}
              inputProps={{ min: 0, max: adDetails.price }}
              helperText={
                adDetails.minOfferAmount &&
                parseFloat(adDetails.minOfferAmount) >
                  parseFloat(adDetails.price)
                  ? "Minimum offer must be less than or equal to Price"
                  : ""
              }
              error={
                adDetails.minOfferAmount &&
                parseFloat(adDetails.minOfferAmount) >
                  parseFloat(adDetails.price)
              }
            />
          )}
          <Button
            type="submit"
            className="main-button-style"
            sx={{
              mt: 2,
              ...mainButtonStyles,
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span>Updating...</span>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </>
            ) : (
              <>
                <span>Update Ad</span>
                <ArrowForwardOutlinedIcon />
              </>
            )}
          </Button>
        </form>
      </Box>
      <AdStatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        adID={statusModal.adID}
        setStatusModal={setStatusModal}
      />
    </Container>
  );
}
